import React, { useEffect } from 'react';
import { X, Check, Phone } from 'lucide-react';
import "../styles/Modal.css";

const InfoModal = ({ show, handleClose, service }) => {
    // Escape para cerrar y bloqueo del scroll de la página mientras está abierto
    useEffect(() => {
        if (!show) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') handleClose();
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [show, handleClose]);

    if (!show) return null;

    const openContactModal = () => {
        const contactModalElement = document.getElementById('contactModal');
        if (contactModalElement) {
            // Asegurar que el modal esté visible
            contactModalElement.style.display = 'block';
            contactModalElement.classList.add('show');
            contactModalElement.setAttribute('aria-hidden', 'false');
            contactModalElement.setAttribute('aria-modal', 'true');
            contactModalElement.setAttribute('role', 'dialog');

            // Crear backdrop si no existe
            let backdrop = document.querySelector('.modal-backdrop');
            if (!backdrop) {
                backdrop = document.createElement('div');
                backdrop.className = 'modal-backdrop fade';
                document.body.appendChild(backdrop);
            }

            // Mostrar backdrop con animación
            setTimeout(() => {
                backdrop.classList.add('show');
            }, 10);

            // Configurar body
            document.body.classList.add('modal-open');
            document.body.style.overflow = 'hidden';

            // Agregar evento para cerrar con Escape
            const handleEscape = (e) => {
                if (e.key === 'Escape') {
                    closeContactModal();
                }
            };

            // Agregar evento para cerrar con click en backdrop
            backdrop.onclick = closeContactModal;

            document.addEventListener('keydown', handleEscape);

            // Guardar referencia para limpiar después
            contactModalElement.dataset.escapeHandler = 'true';
        }
    };

    const closeContactModal = () => {
        const contactModalElement = document.getElementById('contactModal');
        if (contactModalElement) {
            // Ocultar modal
            contactModalElement.classList.remove('show');

            setTimeout(() => {
                contactModalElement.style.display = 'none';
                contactModalElement.setAttribute('aria-hidden', 'true');
                contactModalElement.removeAttribute('aria-modal');
                contactModalElement.removeAttribute('role');

                // Remover backdrop
                const backdrop = document.querySelector('.modal-backdrop');
                if (backdrop) {
                    backdrop.classList.remove('show');
                    setTimeout(() => backdrop.remove(), 150);
                }

                // Restaurar body
                document.body.classList.remove('modal-open');
                document.body.style.overflow = '';
                document.body.removeAttribute('style');

                // Limpiar eventos
                document.removeEventListener('keydown', closeContactModal);
            }, 150);
        }
    };

    const handleRequestQuote = () => {
        // Primero cerrar este modal
        handleClose();

        // Remover cualquier backdrop residual
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());

        // Restaurar el scroll del body
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';

        // Pequeño delay para que el modal se cierre completamente
        setTimeout(() => {
            openContactModal();
        }, 200);
    };

    const handleModalClose = () => {
        // Limpiar cualquier backdrop residual
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());

        // Restaurar el body
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';

        // Cerrar el modal
        handleClose();
    };

    return (
      <div
        className="bmodal"
        role="dialog"
        aria-modal="true"
        aria-label={`${service.title} details`}
        onClick={(e) => {
          if (e.target === e.currentTarget) handleModalClose();
        }}
      >
        <div className="bmodal__panel bmodal__panel--wide">
          <div className="bmodal__head">
            <div>
              <span className="bmodal__eyebrow">Service detail</span>
              <h3 className="bmodal__title">{service.title}</h3>
            </div>
            <button
              type="button"
              className="bmodal__close"
              onClick={handleModalClose}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="bmodal__body">
            <div className="imodal__grid">
              <aside className="imodal__aside">
                <div className="imodal__icon" aria-hidden="true">
                  {service.icon}
                </div>
                <span className="mono-label" style={{ color: 'var(--slate)' }}>
                  Overview
                </span>
                <p style={{ marginTop: '0.6rem' }}>{service.description}</p>
              </aside>

              <div>
                <h4 className="imodal__h">What this service covers</h4>
                <p>{service.additionalInfo.fullDescription}</p>

                <h4 className="imodal__h">Included in every visit</h4>
                <ul className="imodal__features">
                  {service.additionalInfo.features.map((feature, index) => (
                    <li key={index}>
                      <Check size={18} strokeWidth={3} aria-hidden="true" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="imodal__quote">
                  <span className="mono-label">Every job is quoted</span>
                  <p>
                    Price depends on the space, its condition and how often you
                    want us there. Tell us what you need and we come back with a
                    figure — free, no obligation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bmodal__foot">
            <a href="tel:+13109254824" className="btn-ghost">
              <Phone size={16} aria-hidden="true" />
              <span className="mono">(310) 925-4824</span>
            </a>
            <button
              type="button"
              className="btn-sun"
              onClick={handleRequestQuote}
            >
              Request a quote
            </button>
          </div>
        </div>
      </div>
    );
};

export default InfoModal;
